import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  api,
  ApiError,
  Calendar,
  CreateCalendarInput,
  UpdateCalendarInput,
} from "../lib/api";

const CALENDAR_COLORS = [
  { value: "#ef4444", label: "Red" },
  { value: "#f97316", label: "Orange" },
  { value: "#f59e0b", label: "Amber" },
  { value: "#84cc16", label: "Lime" },
  { value: "#22c55e", label: "Green" },
  { value: "#14b8a6", label: "Teal" },
  { value: "#3b82f6", label: "Blue" },
  { value: "#8b5cf6", label: "Violet" },
  { value: "#ec4899", label: "Pink" },
  { value: "#6b7280", label: "Gray" },
];

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time" },
  { value: "America/Chicago", label: "Central Time" },
  { value: "America/Denver", label: "Mountain Time" },
  { value: "America/Los_Angeles", label: "Pacific Time" },
  { value: "America/Anchorage", label: "Alaska Time" },
  { value: "Pacific/Honolulu", label: "Hawaii Time" },
  { value: "UTC", label: "UTC" },
];

interface CalendarFormProps {
  calendar?: Calendar;
  onClose: () => void;
  onSuccess: () => void;
}

function CalendarForm({ calendar, onClose, onSuccess }: CalendarFormProps) {
  const isEdit = !!calendar;
  const [name, setName] = useState(calendar?.name ?? "");
  const [calendarType, setCalendarType] = useState<"staff" | "resource">(
    calendar?.calendarType ?? "staff"
  );
  const [description, setDescription] = useState(calendar?.description ?? "");
  const [color, setColor] = useState(calendar?.color ?? "#3b82f6");
  const [timezone, setTimezone] = useState(
    calendar?.timezone ?? "America/New_York"
  );
  const [error, setError] = useState("");

  const createMutation = useMutation({
    mutationFn: (data: CreateCalendarInput) => api.calendars.create(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to create calendar");
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateCalendarInput) =>
      api.calendars.update(calendar!.id, data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to update calendar");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Calendar name is required");
      return;
    }

    const data: CreateCalendarInput = {
      name: name.trim(),
      calendarType,
      description: description.trim() || null,
      color,
      timezone,
    };

    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">
          {isEdit ? "Edit Calendar" : "Add New Calendar"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="calendarName"
              className="block text-sm font-medium text-gray-700"
            >
              Calendar name
            </label>
            <input
              id="calendarName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            />
          </div>

          <div>
            <label
              htmlFor="calendarType"
              className="block text-sm font-medium text-gray-700"
            >
              Type
            </label>
            <select
              id="calendarType"
              value={calendarType}
              onChange={(e) =>
                setCalendarType(e.target.value as "staff" | "resource")
              }
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            >
              <option value="staff">Staff</option>
              <option value="resource">Resource</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700"
            >
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="color"
                className="block text-sm font-medium text-gray-700"
              >
                Color
              </label>
              <div className="mt-1 flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-md border border-gray-300"
                  style={{ backgroundColor: color }}
                />
                <select
                  id="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                >
                  {CALENDAR_COLORS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label
                htmlFor="timezone"
                className="block text-sm font-medium text-gray-700"
              >
                Timezone
              </label>
              <select
                id="timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending
                ? "Saving..."
                : isEdit
                  ? "Save Changes"
                  : "Create Calendar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DeleteConfirmModalProps {
  calendar: Calendar;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  error?: string;
}

function DeleteConfirmModal({
  calendar,
  onClose,
  onConfirm,
  isPending,
  error,
}: DeleteConfirmModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
        <h2 className="text-xl font-semibold mb-4">Delete Calendar?</h2>
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete &quot;{calendar.name}&quot;? This will
          deactivate the calendar and it will no longer be available for
          scheduling.
        </p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CalendarRow({
  calendar,
  onEdit,
  onDelete,
}: {
  calendar: Calendar;
  onEdit: (calendar: Calendar) => void;
  onDelete: (calendar: Calendar) => void;
}) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-3">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: calendar.color }}
          />
          <span className="font-medium text-gray-900">{calendar.name}</span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            calendar.calendarType === "staff"
              ? "bg-blue-100 text-blue-800"
              : "bg-purple-100 text-purple-800"
          }`}
        >
          {calendar.calendarType === "staff" ? "Staff" : "Resource"}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div
          className="w-6 h-6 rounded-md border border-gray-300"
          style={{ backgroundColor: calendar.color }}
          title={calendar.color}
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        {calendar.isActive ? (
          <span className="text-green-600">Active</span>
        ) : (
          <span className="text-gray-400">Inactive</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
        <button
          onClick={() => onEdit(calendar)}
          className="text-amber-600 hover:text-amber-700 mr-4"
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(calendar)}
          className="text-red-600 hover:text-red-700"
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

export function CalendarsPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState<Calendar | null>(null);
  const [deletingCalendar, setDeletingCalendar] = useState<Calendar | null>(
    null
  );
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["calendars"],
    queryFn: () => api.calendars.list(),
  });

  const [deleteError, setDeleteError] = useState("");

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.calendars.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendars"] });
      setDeletingCalendar(null);
      setDeleteError("");
    },
    onError: (err) => {
      setDeleteError(
        err instanceof ApiError ? err.message : "Failed to delete calendar"
      );
    },
  });

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["calendars"] });
  };

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">
            {error instanceof ApiError
              ? error.message
              : "Failed to load calendars"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Staff Calendars</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
        >
          Add Calendar
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-pulse text-gray-500">Loading calendars...</div>
        </div>
      ) : data?.data.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">
            No calendars yet. Add your first calendar to get started.
          </p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Color
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.data.map((calendar) => (
                <CalendarRow
                  key={calendar.id}
                  calendar={calendar}
                  onEdit={setEditingCalendar}
                  onDelete={setDeletingCalendar}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <CalendarForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleSuccess}
        />
      )}

      {editingCalendar && (
        <CalendarForm
          calendar={editingCalendar}
          onClose={() => setEditingCalendar(null)}
          onSuccess={handleSuccess}
        />
      )}

      {deletingCalendar && (
        <DeleteConfirmModal
          calendar={deletingCalendar}
          onClose={() => {
            setDeletingCalendar(null);
            setDeleteError("");
          }}
          onConfirm={() => deleteMutation.mutate(deletingCalendar.id)}
          isPending={deleteMutation.isPending}
          error={deleteError}
        />
      )}
    </div>
  );
}
